import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  ChevronDownIcon as HeroChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getFilteredNavigation, navigationConfig, type NavigationItem } from '@/config/navigation';
import type { AdminUser } from '@/services/$AuthService';
import { getAdminRole } from '@/utils/adminAccess';
import { cn } from '@/utils/cn';

const ChevronDown = HeroChevronDownIcon as React.ComponentType<{
  className?: string;
}>;
const ChevronLeft = ChevronLeftIcon as React.ComponentType<{
  className?: string;
}>;
const ChevronRight = ChevronRightIcon as React.ComponentType<{
  className?: string;
}>;
const Close = XMarkIcon as React.ComponentType<{ className?: string }>;
const Logout = ArrowRightOnRectangleIcon as React.ComponentType<{
  className?: string;
}>;
const Shield = ShieldCheckIcon as React.ComponentType<{ className?: string }>;

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
  onRequestLogout: () => void;
  userAuth: {
    user: AdminUser | null;
  };
}

interface NavItemProps {
  item: NavigationItem;
  pathname: string;
  isCollapsed: boolean;
  onNavigate?: () => void;
  level?: number;
}

const ROLE_LABELS: Record<string, string> = {
  manager: 'مدیر',
  superadmin: 'مدیر ارشد',
  admin: 'ادمین',
};

const NAV_DESCRIPTIONS: Record<string, string> = {
  dashboard: 'نمای کلی سامانه',
  data: 'داده‌ها و شاخص‌ها',
  'options-management': 'تنظیمات بازار اختیار',
  users: 'کاربران و دسترسی‌ها',
  admins: 'نقش‌ها و مجوزها',
  finance: 'پرداخت‌ها و تراکنش‌ها',
  subscriptions: 'پلن‌ها و سفارش‌ها',
  referrals: 'کمپین‌ها و پاداش‌ها',
  campaigns: 'گردونه یلدا و جوایز',
  courses: 'آموزش و محتوا',
  analysis: 'تحلیل‌های منتشرشده',
  media: 'فایل‌ها و رسانه‌ها',
  tickets: 'پیگیری درخواست‌ها',
  'public-support': 'پشتیبانی عمومی',
  consulting: 'درخواست‌های مشاوره',
  faq: 'دانش و پرسش‌ها',
  notifications: 'پیام‌ها و اعلان‌ها',
  agent: 'عامل و دانش هوشمند',
  portfolio: 'پرتفوهای کاربران',
  'portfolio-management': 'تعاریف سبدگردانی',
};

const isPathActive = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const NavItem: React.FC<NavItemProps> = ({
  item,
  pathname,
  isCollapsed,
  onNavigate,
  level = 0,
}) => {
  const visibleChildren = (item.children ?? []).filter(child => child.path !== item.path);
  const isSelfActive = isPathActive(pathname, item.path);
  const isChildActive = visibleChildren.some(child => isPathActive(pathname, child.path));
  const isActive = isSelfActive || isChildActive;
  const [isExpanded, setIsExpanded] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) setIsExpanded(true);
  }, [isChildActive]);

  const Icon = item.icon;
  const linkClasses = cn(
    'group flex min-h-12 min-w-0 flex-1 items-center rounded-2xl text-sm transition',
    isCollapsed ? 'justify-center px-0' : 'gap-3 px-3 py-2',
    level > 0 && !isCollapsed && 'min-h-11 rounded-xl pr-4',
    isActive
      ? 'bg-white text-[#0f2533] shadow-lg shadow-slate-950/[0.15]'
      : 'text-slate-200/[0.82] hover:bg-white/10 hover:text-white'
  );

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Link
          to={item.path}
          title={isCollapsed ? item.labelPersian : undefined}
          onClick={onNavigate}
          className={linkClasses}
          aria-current={pathname === item.path ? 'page' : undefined}
        >
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition',
              !isCollapsed && 'bg-white/[0.08]'
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
          {!isCollapsed && (
            <span className="min-w-0 flex-1">
              <span className="block truncate font-black">{item.labelPersian}</span>
              {level === 0 && (
                <span className="mt-0.5 block truncate text-[11px] font-medium opacity-65">
                  {NAV_DESCRIPTIONS[item.id] ?? 'مدیریت این بخش'}
                </span>
              )}
            </span>
          )}
        </Link>

        {!isCollapsed && visibleChildren.length > 0 && (
          <button
            type="button"
            className="flex h-10 w-9 shrink-0 items-center justify-center rounded-xl text-slate-300 transition hover:bg-white/10 hover:text-white"
            onClick={() => setIsExpanded(value => !value)}
            aria-label={`${isExpanded ? 'بستن' : 'باز کردن'} زیرمنوی ${item.labelPersian}`}
            aria-expanded={isExpanded}
          >
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          </button>
        )}
      </div>

      {!isCollapsed && visibleChildren.length > 0 && isExpanded && (
        <div className="mr-5 space-y-1 border-r border-white/10 pr-2">
          {visibleChildren.map(child => (
            <NavItem
              key={child.id}
              item={child}
              pathname={pathname}
              isCollapsed={false}
              onNavigate={onNavigate}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SidebarContent: React.FC<{
  isCollapsed: boolean;
  navigation: NavigationItem[];
  pathname: string;
  adminName: string;
  roleLabel: string;
  onToggleCollapse: () => void;
  onNavigate?: () => void;
  onRequestLogout: () => void;
  mobile?: boolean;
}> = ({
  isCollapsed,
  navigation,
  pathname,
  adminName,
  roleLabel,
  onToggleCollapse,
  onNavigate,
  onRequestLogout,
  mobile = false,
}) => (
  <>
    <div
      className={cn(
        'mb-7 flex items-center',
        isCollapsed ? 'justify-center' : 'justify-between px-1'
      )}
    >
      {!isCollapsed ? (
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-400/[0.14] text-emerald-200 ring-1 ring-white/10">
            <Shield className="h-5 w-5" />
          </span>
          <div>
            <div className="text-xl font-black leading-6 text-white">بامبو</div>
            <div className="mt-0.5 text-[11px] font-bold text-emerald-100/70">پنل مدیریت</div>
          </div>
        </div>
      ) : (
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-400/[0.14] text-emerald-200 ring-1 ring-white/10">
          <Shield className="h-5 w-5" />
        </span>
      )}

      <button
        type="button"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-emerald-50/80 transition hover:bg-white/10 hover:text-white"
        onClick={onToggleCollapse}
        aria-label={mobile ? 'بستن منو' : isCollapsed ? 'باز کردن منو' : 'جمع کردن منو'}
      >
        {mobile ? (
          <Close className="h-5 w-5" />
        ) : isCollapsed ? (
          <ChevronLeft className="h-5 w-5" />
        ) : (
          <ChevronRight className="h-5 w-5" />
        )}
      </button>
    </div>

    <nav className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pb-3">
      {navigation.map(item => (
        <NavItem
          key={item.id}
          item={item}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
      ))}
    </nav>

    <div
      className={cn(
        'mt-4',
        isCollapsed
          ? 'flex justify-center'
          : 'rounded-2xl border border-white/10 bg-white/[0.06] p-3'
      )}
    >
      {isCollapsed ? (
        <button
          type="button"
          title="خروج از حساب کاربری"
          aria-label="خروج از حساب کاربری"
          onClick={onRequestLogout}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white transition hover:bg-white/[0.15]"
        >
          <Logout className="h-5 w-5" />
        </button>
      ) : (
        <>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-emerald-100">
              <Shield className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-black text-white">{adminName}</div>
              <div className="mt-1 inline-flex max-w-full rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-black text-emerald-100">
                <span className="truncate">{roleLabel}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onRequestLogout}
            className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-white/10 text-sm font-black text-white transition hover:bg-white/[0.15]"
          >
            <Logout className="h-5 w-5" />
            خروج از حساب کاربری
          </button>
        </>
      )}
    </div>
  </>
);

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  isCollapsed,
  onClose,
  onToggleCollapse,
  onRequestLogout,
  userAuth,
}) => {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const userRole = getAdminRole(userAuth.user) || 'user';
  const userPermissions = userAuth.user?.permissions ?? [];
  const navigation = useMemo(
    () => getFilteredNavigation(navigationConfig, userPermissions, userRole),
    [userPermissions, userRole]
  );
  const adminName = userAuth.user?.name || userAuth.user?.username || 'مدیر سامانه';
  const roleLabel = ROLE_LABELS[userRole] ?? 'کاربر پنل';

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;
    if (isOpen) onClose();
  }, [isOpen, location.pathname, onClose]);

  return (
    <>
      <aside
        className={cn(
          'fixed inset-y-0 right-0 z-30 hidden flex-col border-l border-white/10 bg-[#0f2533] py-5 text-white shadow-2xl shadow-slate-950/20 transition-[width,padding] duration-200 lg:flex',
          isCollapsed ? 'w-24 px-3' : 'w-72 px-4'
        )}
      >
        <SidebarContent
          isCollapsed={isCollapsed}
          navigation={navigation}
          pathname={location.pathname}
          adminName={adminName}
          roleLabel={roleLabel}
          onToggleCollapse={onToggleCollapse}
          onRequestLogout={onRequestLogout}
        />
      </aside>

      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={onClose}
            aria-label="بستن منو"
          />
          <aside className="absolute inset-y-0 right-0 flex w-[86vw] max-w-[340px] flex-col bg-[#0f2533] px-4 py-5 text-white shadow-2xl">
            <SidebarContent
              isCollapsed={false}
              navigation={navigation}
              pathname={location.pathname}
              adminName={adminName}
              roleLabel={roleLabel}
              onToggleCollapse={onClose}
              onNavigate={onClose}
              onRequestLogout={onRequestLogout}
              mobile
            />
          </aside>
        </div>
      )}
    </>
  );
};
